import { rgba } from 'polished';
import React, { ComponentType, FC, forwardRef, ForwardRefRenderFunction, HTMLAttributes, ReactNode, Ref } from 'react';
import styled from 'styled-components';
import Star from '@oberoninternal/travelbase-ds/svg/Star';
import Button from '@oberoninternal/travelbase-ds/components/action/Button';
import Img, { BaseImgProps, RatioImgProps } from '@oberoninternal/travelbase-ds/components/figure/Img';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import Title from '@oberoninternal/travelbase-ds/components/primitive/Title';

import { getAssignmentColor, getFontCss } from '@oberoninternal/travelbase-ds/constants/theme';
import VideoLabel from '@oberoninternal/travelbase-ds/components/primitive/VideoLabel';

interface Props extends HTMLAttributes<HTMLDivElement> {
    photo: BaseImgProps;
    discountName?: ReactNode;
    discountIcon?: ReactNode;
    discountDescription?: ReactNode;
    ref?: Ref<HTMLDivElement>;
    videoUrl?: string | null;
    fullCircleImageUrl?: string | null;
    rightIcon?: ReactNode;
    leftIcon?: ReactNode[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    as?: keyof React.JSX.IntrinsicElements | React.ComponentType<React.PropsWithChildren<any>>;
}

interface RatingProps {
    rating?: number;
}

interface PriceProps {
    totalPrice?: ReactNode;
    totalNights?: ReactNode;
    totalPriceText?: ReactNode;
    nightPrice?: ReactNode;
    nightPriceText?: ReactNode;
    originalPrice?: ReactNode;
    discountPercentage?: number;
}
interface RelatedProps {
    related?: number;
}

const UnitTile: ForwardRefRenderFunction<HTMLDivElement, Props> = (
    {
        photo,
        discountName,
        discountIcon,
        discountDescription,
        videoUrl,
        fullCircleImageUrl,
        children,
        rightIcon,
        leftIcon,
        ...props
    },
    ref
) => (
    <Container {...props} ref={ref}>
        <ImageWrapper>
            {discountName && (
                <DiscountButton variant="outline" size="small">
                    <DiscountIcon>{discountIcon}</DiscountIcon>
                    {discountName}
                </DiscountButton>
            )}
            {discountDescription && discountName && (
                <DiscountHover>
                    <Body variant="small">{discountDescription}</Body>
                    <Triangle />
                </DiscountHover>
            )}
            {rightIcon && <RightIconWrapper>{rightIcon}</RightIconWrapper>}
            <LeftIconWrapper>
                {leftIcon?.map((icon, i) => (
                    <React.Fragment key={i}>{icon}</React.Fragment>
                ))}
            </LeftIconWrapper>
            <VideoLabel videoUrl={videoUrl} fullCircleImageUrl={fullCircleImageUrl} />

            <StyledImg layout="ratio" ratio={3 / 4} {...photo} />
        </ImageWrapper>
        {children}
    </Container>
);

export const Rating: FC<React.PropsWithChildren<RatingProps>> = ({ rating }) =>
    rating ? (
        <RatingContainer>
            <StyledRating variant="small">
                <StyledStar />
                <Number>{rating}</Number>
            </StyledRating>
        </RatingContainer>
    ) : null;

export const Price: FC<React.PropsWithChildren<PriceProps>> = ({
    totalPrice,
    totalNights,
    totalPriceText,
    nightPrice,
    nightPriceText,
    originalPrice,
    discountPercentage,
}) => (
    <>
        {totalPrice ? (
            <TotalPriceWrapper>
                {totalPrice && <TotalPrice>{totalPrice}</TotalPrice>}
                {totalPriceText && <TotalPriceText as="span">{totalPriceText}</TotalPriceText>}
                {originalPrice && <OriginalPrice>{originalPrice}</OriginalPrice>}
                {originalPrice && <DiscountTag>{discountPercentage}%</DiscountTag>}
                {totalNights && <TotalNights>{totalNights}</TotalNights>}
            </TotalPriceWrapper>
        ) : nightPrice ? (
            <NightPrice>
                {nightPrice && nightPrice} {nightPriceText && <span>{nightPriceText}</span>}
            </NightPrice>
        ) : null}
    </>
);

export const Related: FC<React.PropsWithChildren<RelatedProps>> = ({ related }) => (
    <RelatedContainer>{related}</RelatedContainer>
);

export const Byline: FC<React.PropsWithChildren<unknown>> = ({ children }) => <StyledByline>{children}</StyledByline>;

const Container = styled.div`
    text-decoration: none;
    color: inherit;
    display: flex;
    flex-direction: column;
    width: 44.8rem;
    margin-right: ${({ theme }) => theme.spacing['50_Semi']};

    scroll-snap-align: start;

    &:last-of-type {
        margin-right: 22.4rem;
    }

    > div:first-of-type {
        position: relative;
    }

    > div:nth-of-type(2) {
        display: flex;
        align-items: center;
    }
    margin-bottom: 0px !important;
`;

const ImageWrapper = styled.div`
    margin-bottom: ${({ theme }) => theme.spacing['30_Small']};
`;

const StyledImg = styled(Img)<RatioImgProps>`
    margin: 0;
    position: relative;
`;

const DiscountIcon = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;

    > svg {
        margin-right: 0.3rem;
    }
`;

const DiscountHover = styled.div`
    max-width: 27.7rem;
    background: ${({ theme }) => theme.colors.neutral['60']};
    border-radius: 0.8rem;
    color: ${({ theme }) => theme.colors.neutral['0']};
    font-family: ${({ theme }) => theme.fontFamily.body};
    font-size: 1.4rem;
    padding: ${({ theme }) => theme.spacing['40_Standard']};
    position: absolute;
    top: ${({ theme }) => theme.spacing['70_XLarge']};
    left: ${({ theme }) => theme.spacing['30_Small']};
    opacity: 0;
    transition: opacity 0.25s;
    z-index: 1;
`;

const DiscountButton = styled(Button)`
    z-index: 1;
    position: absolute;
    left: ${({ theme }) => theme.spacing['30_Small']};
    top: ${({ theme }) => theme.spacing['30_Small']};
    font-family: ${({ theme }) => theme.fontFamily.body};
    color: ${({ theme }) => theme.colors.primary['70']};
    --border-color: ${({ theme }) => rgba(theme.colors.primary['70'], 0.24)};
    border-radius: 0.8rem;
    text-align: center;
    box-shadow: 0 0 0 1px var(--border-color);

    :hover,
    :focus,
    :active {
        background: ${({ theme }) => theme.colors.neutral['0']};
        color: ${({ theme }) => theme.colors.primary['80']};

        + ${DiscountHover} {
            opacity: 0.9;
        }
    }
`;

const RatingContainer = styled.div`
    display: flex;
    align-items: center;
`;

const StyledRating = styled(Body)`
    display: flex;
    align-items: baseline;
    color: ${({ theme }) =>
        theme.colorAssignments.rating
            ? getAssignmentColor(theme, theme.colorAssignments.rating)
            : theme.colors.secondary['30']};
    margin-right: ${({ theme }) => theme.spacing['30_Small']};
    margin-bottom: ${({ theme }) => theme.spacing['30_Small']};
`;

export const StyledStar = styled(Star)`
    margin: 0;
    display: inline-block;
    margin-right: ${({ theme }) => theme.spacing['20_Tiny']};
`;

const Number = styled.span`
    margin: 0;
    font-family: ${({ theme }) => theme.fontFamily.body};
    align-self: start;
`;

export const Text = styled(Body)`
    margin-bottom: ${({ theme }) => theme.spacing['30_Small']};
    margin-top: ${({ theme }) => theme.spacing['20_Tiny']};
`;

const TotalPriceWrapper = styled.div`
    display: flex;
    font-size: 1.6rem;
    font-weight: 700;
    align-items: center;
`;

const TotalPrice = styled(Body)`
    color: ${({ theme }) => theme.colors.neutral['100']};
    font-size: 1.6rem;
    font-weight: 700;
    margin-right: 0.8rem;
    * {
        color: ${({ theme }) => theme.colors.neutral['100']};
        font-size: 1.6rem;
        font-weight: 700;
    }
`;

const TotalPriceText = styled(Body).attrs({ variant: 'small' })`
    display: inline;
    margin-right: ${({ theme }) => theme.spacing['20_Tiny']};
`;

const NightPrice = styled(TotalPrice)`
    font-size: 1.4rem;
`;

const OriginalPrice = styled.span`
    text-decoration: line-through;
    margin-right: 0.8rem;
    font-size: 1.6rem;
    color: ${({ theme }) => theme.colors.neutral['40']};
`;

const Triangle = styled.div`
    width: 0;
    height: 0;
    border-left: 1rem solid transparent;
    border-right: 1rem solid transparent;
    border-bottom: 1rem solid ${({ theme }) => theme.colors.neutral['60']};
    position: absolute;
    top: -0.8rem;
`;

const RelatedContainer = styled.div`
    display: flex;
    font-family: ${({ theme }) => theme.fontFamily.body};
    margin-top: 2rem;
    font-size: 1.6rem;
`;

const StyledByline = styled(Title)`
    font-weight: 300;
`;

const DiscountTag = styled.div`
    display: flex;
    align-items: center;
    padding: 0 0.4rem 0 0.6rem;
    border: 1px solid rgba(62, 113, 169, 0.25);
    border-radius: 0.4rem;
    font-size: ${({ theme }) => getFontCss(theme.fontSizes.body.tiny)};
    color: ${({ theme }) => theme.colors.primary['60']};
    margin-right: ${({ theme }) => theme.spacing['20_Tiny']};
`;

const TotalNights = styled.span`
    font-size: 1.6rem;
    color: ${({ theme }) => theme.colors.neutral['40']};
`;

const RightIconWrapper = styled.div`
    z-index: 1;
    position: absolute;
    right: 1.2rem;
    top: 1.2rem;
`;

const LeftIconWrapper = styled.div`
    z-index: 1;
    position: absolute;
    left: 1.2rem;
    bottom: 1.2rem;
    display: flex;
    gap: 0.5rem;
`;

export default forwardRef(UnitTile) as ComponentType<React.PropsWithChildren<Props>>;
